import type { ComponentChildren, Ref } from 'preact';
import mergeRefs from 'merge-refs';
import { Signal } from '@preact/signals';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import { cn } from 'utils/cn.tsx';
import { useFocusTrap } from 'utils/focusTrap';
import { localize } from 'i18n/localize';

const $t = localize();

// To handle multiple modals and setting overflow hidden on body
const openedModalIds = new Signal<string[]>([]);

export type Props = {
	onClose?: () => void;
	onClickAway?: () => void;
	isMobileFullscreen?: boolean;
	children?: ComponentChildren;
	className?: string;
	forwardedRef?: Ref<HTMLDivElement>;
};

export function ModalEmpty({
	onClose,
	onClickAway,
	children,
	className = '',
	isMobileFullscreen,
	forwardedRef,
}: Props) {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const idRef = useRef(Math.random().toString()); // Use id is not working because of the portal
	const handleOutsideClick = useCallback((event: MouseEvent) => {
		if (wrapperRef.current && !wrapperRef.current.contains(event.target as HTMLDivElement)) {
			onClickAway?.();
		}
	}, []);
	const [firstElementRef, lastElementRef, containerElementRef] = useFocusTrap();

	useEffect(() => {
		// focus inside modal on open
		firstElementRef.current?.focus();

		const handleKeyPress = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose?.();
			}
		};

		openedModalIds.peek().push(idRef.current);
		if (globalThis.document.body.parentElement) {
			globalThis.document.body.parentElement.style.scrollbarGutter = 'stable';
		}
		globalThis.document.body.style.overflow = 'hidden';

		globalThis.document.addEventListener('keydown', handleKeyPress);
		return () => {
			globalThis.document.removeEventListener('keydown', handleKeyPress);

			openedModalIds.value = openedModalIds.peek().filter((modalId) => modalId !== idRef.current);
			if (openedModalIds.peek().length === 0) {
				globalThis.document.body.style.overflow = '';
				if (globalThis.document.body.parentElement) {
					globalThis.document.body.parentElement.style.scrollbarGutter = '';
				}
			}
		};
	}, []);

	return (
		<div
			class="z-modal fixed left-0 right-0 top-0 bottom-0 flex flex-col bg-neutral-35"
			onClickCapture={handleOutsideClick}
		>
			<div class={cn('h-[140px] max-h-[20%]', isMobileFullscreen && 'hidden md:block')}></div>
			<div
				ref={mergeRefs(wrapperRef, forwardedRef, containerElementRef)}
				class={cn(
					'grow-0 shadow-md self-center bg-neutral-white md:rounded-2xl',
					isMobileFullscreen ? 'h-full md:h-auto md:max-h-[80vh]' : 'max-h-[80vh] rounded-t-2xl',
					className,
				)}
				role="dialog"
				aria-modal="true"
			>
				<span title={$t.modal.windowStartAnchorTitle} ref={firstElementRef} tabIndex={0} />
				{children}
				<span title={$t.modal.windowEndAnchorTitle} ref={lastElementRef} tabIndex={0} />
			</div>
			<div class={cn('md:grow-[5]', isMobileFullscreen && 'hidden md:block')}></div>
		</div>
	);
}
